import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { UtilisationMetrics } from '@/types/utilisation';

interface DepartmentSummarySectionProps {
  employeeMetrics: UtilisationMetrics[];
}

export const DepartmentSummarySection: React.FC<DepartmentSummarySectionProps> = ({
  employeeMetrics,
}) => {
  // Group employee metrics by department
  const groupedMetrics = useMemo(() => {
    const grouped: { [key: string]: typeof employeeMetrics } = {};
    employeeMetrics.forEach((metric) => {
      if (!grouped[metric.department]) {
        grouped[metric.department] = [];
      }
      grouped[metric.department].push(metric);
    });
    return grouped;
  }, [employeeMetrics]);

  // Calculate department summaries
  const departmentSummaries = useMemo(() => {
    const summaries: {
      [key: string]: {
        totalHours: number;
        billableHours: number;
        employeeCount: number;
        avgUtilisation: number;
      };
    } = {};

    Object.entries(groupedMetrics).forEach(([dept, metrics]) => {
      const totalHours = metrics.reduce((sum, m) => sum + m.totalHours, 0);
      const billableHours = metrics.reduce((sum, m) => sum + m.billableHours, 0);
      const avgUtilisation =
        metrics.length > 0
          ? metrics.reduce((sum, m) => sum + m.utilisationRate, 0) / metrics.length
          : 0;

      summaries[dept] = {
        totalHours,
        billableHours,
        employeeCount: metrics.length,
        avgUtilisation,
      };
    });

    return summaries;
  }, [groupedMetrics]);

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="text-base">Department Summary</CardTitle>
        <CardDescription>Utilisation by department</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          <ScrollAreaViewport className="h-full pr-4">
            <div className="space-y-4">
              {Object.entries(departmentSummaries).map(([dept, summary]) => (
                <div key={dept} className="space-y-2 pb-4 border-b last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{dept}</span>
                    <Badge variant="secondary">
                      {summary.employeeCount}{' '}
                      {summary.employeeCount === 1 ? 'employee' : 'employees'}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Utilisation</span>
                      <span className="font-medium">
                        {(summary.avgUtilisation * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full transition-all',
                          summary.avgUtilisation >= 0.75
                            ? 'bg-success-500'
                            : summary.avgUtilisation >= 0.5
                              ? 'bg-warning-500'
                              : 'bg-error-500'
                        )}
                        style={{ width: `${summary.avgUtilisation * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Total: </span>
                      <span className="font-medium">{summary.totalHours.toFixed(1)}h</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Billable: </span>
                      <span className="font-medium text-success-600 dark:text-success-400">
                        {summary.billableHours.toFixed(1)}h
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
